/* Advisor simulator — exercise the live advisor pipeline without audio.
 *
 * Two modes share one page:
 *
 *   * Bulk      paste a CUSTOMER:/AGENT: transcript, hit Run, watch
 *               cards stream in as the bot replays the turns.
 *   * Step      start an empty session, then type one turn at a time
 *               and watch the advisor react like a chat.
 *
 * Both POST to /api/simulator/* which proxies to pipe-agent. The bot
 * creates a synthetic is_test=1 call row, runs the real AdvisorRunner
 * with that customer's offers + competitors, and posts transcripts +
 * advice through the same EventSink production uses. The right-hand
 * pane is the same LiveAdvice + Transcript components AgentDesk renders
 * during a real call, both subscribed to the synthetic call's existing
 * /api/calls/:id/stream SSE feed — what shows up here is byte-for-byte
 * what an agent would see in production.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "./AppShell";
import { LiveAdvice } from "./agent-desk/LiveAdvice";
import {
  SIM_SCENARIOS,
  type SimScenario,
  scenarioToTranscriptText,
} from "./agent-desk/sim-scenarios";
import { API_URL, apiFetch, fmtTime } from "./lib/api";

type Mode = "bulk" | "step";
type Role = "customer" | "agent";
type Turn = { role: Role; text: string };

type CustomerOption = {
  id: number;
  first_name: string | null;
  last_name: string | null;
  business_name: string | null;
  phone: string;
};

const SAMPLE_TRANSCRIPT = `CUSTOMER: Hi, I'm calling because my bill jumped this month and I'm pretty frustrated.
AGENT: I hear you, let me pull up your account and take a look.
CUSTOMER: It went up like thirty bucks. Nobody told me anything.
AGENT: I see what happened — a promo expired on two of your lines.
CUSTOMER: Yeah look I've been with you guys forever. AT&T is offering me forty a line.
AGENT: I appreciate your loyalty. Let me see what I can do for you.
CUSTOMER: Honestly if you can't match it I'm just going to switch over.`;

// Specific validation issues surfaced under the editor instead of
// silently disabling Run. Empty array when transcript is fine.
type ValidationIssue = {
  severity: "error" | "warning";
  message: string;
  line?: number;
};

function validateDraft(raw: string, turns: Turn[]): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (!raw.trim()) {
    issues.push({
      severity: "error",
      message: "Transcript is empty — type or load a scenario.",
    });
    return issues;
  }
  // Walk lines for unknown speaker prefixes — accept the same tags
  // parseTranscript() does + flag others.
  const known = new Set([
    "CUSTOMER", "USER", "CALLER",
    "AGENT", "REP", "HUMAN",
  ]);
  raw.split(/\r?\n/).forEach((rawLine, i) => {
    const line = rawLine.trim();
    if (!line) return;
    const m = line.match(/^([A-Za-z]+)\s*[:|]\s*(.*)$/);
    if (!m) return; // continuation of previous turn — fine
    const tag = (m[1] ?? "").toUpperCase();
    if (!known.has(tag)) {
      issues.push({
        severity: "warning",
        message: `Unknown speaker "${m[1]}" — only CUSTOMER and AGENT are recognised.`,
        line: i + 1,
      });
    }
  });
  if (turns.length === 0) {
    issues.push({
      severity: "error",
      message: 'No valid turns parsed. Each line should start with "CUSTOMER:" or "AGENT:".',
    });
    return issues;
  }
  const hasCustomer = turns.some((t) => t.role === "customer");
  const hasAgent = turns.some((t) => t.role === "agent");
  if (!hasCustomer) {
    issues.push({
      severity: "warning",
      message: "Transcript has no CUSTOMER turns — the advisor reacts to customer signals, so cards may not fire.",
    });
  }
  if (!hasAgent) {
    issues.push({
      severity: "warning",
      message: "Transcript has no AGENT turns — pivot moments like 'let me see what I can do' won't trigger offer_presentation.",
    });
  }
  return issues;
}

// Parse a free-form transcript like "CUSTOMER: hi\nAGENT: hello" into
// turns. Tolerates lower-case, leading whitespace, USER/CALLER as
// aliases for CUSTOMER, REP/HUMAN as aliases for AGENT.
function parseTranscript(raw: string): Turn[] {
  const out: Turn[] = [];
  for (const rawLine of raw.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const m = line.match(/^([A-Za-z]+)\s*[:|]\s*(.*)$/);
    if (!m) {
      // No prefix — append to the previous turn so a transcript that
      // wraps onto multiple lines keeps the right speaker.
      const last = out[out.length - 1];
      if (last) last.text = `${last.text} ${line}`.trim();
      continue;
    }
    const tag = (m[1] ?? "").toUpperCase();
    const text = (m[2] ?? "").trim();
    if (!text) continue;
    let role: Role | null = null;
    if (tag === "CUSTOMER" || tag === "USER" || tag === "CALLER") role = "customer";
    else if (tag === "AGENT" || tag === "REP" || tag === "HUMAN") role = "agent";
    if (role) out.push({ role, text });
  }
  return out;
}

type SavedScenario = {
  id: number;
  label: string;
  category: string;
  transcript_text: string;
  created_at: string;
  updated_at: string;
};

// Tracks "what's currently in the textarea" so Save knows whether to
// create a new row or update an existing saved one. Starters can be
// loaded but never overwritten — saving a starter creates a new copy.
type LoadedSource =
  | null
  | { kind: "starter"; id: string }
  | { kind: "saved"; id: number };

export function AdvisorSimulator() {
  const [mode, setMode] = useState<Mode>("bulk");

  // Shared state
  const [customerId, setCustomerId] = useState<number | "">("");
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [callId, setCallId] = useState<number | undefined>(undefined);
  const [err, setErr] = useState<string | null>(null);

  // Bulk-mode state. Default-load bill_small_bump on first paint —
  // gives a working scenario in one keystroke and shows what the
  // output should look like. Picked because it's short (6 turns) and
  // exercises three variants (intent_root_cause, suggested_approach,
  // offer_presentation). Replaces SAMPLE_TRANSCRIPT.
  const defaultStarter = SIM_SCENARIOS.find((s) => s.id === "bill_small_bump");
  const [draft, setDraft] = useState<string>(
    defaultStarter ? scenarioToTranscriptText(defaultStarter) : "",
  );
  const [defaultDelayMs, setDefaultDelayMs] = useState<number>(1500);
  const [running, setRunning] = useState<boolean>(false);
  const [savedScenarios, setSavedScenarios] = useState<SavedScenario[]>([]);
  const [loaded, setLoaded] = useState<LoadedSource>(
    defaultStarter ? { kind: "starter", id: defaultStarter.id } : null,
  );

  // Step-mode state
  const [stepLive, setStepLive] = useState<boolean>(false);
  const [stepStarting, setStepStarting] = useState<boolean>(false);
  const [stepRole, setStepRole] = useState<Role>("customer");
  const [stepText, setStepText] = useState<string>("");
  const [stepSending, setStepSending] = useState<boolean>(false);
  // Local transcript-so-far rail. Mirrors what's been sent so the
  // composer doesn't have to look at the right pane to remember the
  // last few turns. Cleared on session start/end.
  const [stepSent, setStepSent] = useState<Turn[]>([]);

  // Customer catalog — drives offers/competitors context the advisor
  // sees. Optional: an empty pick means the runner has no eligible
  // offers / no monthly_charge_cents anchor, which is the fastest way
  // to verify the LLM still produces sensible no-data cards.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch<{ customers: CustomerOption[] }>(
          "/api/customers",
        );
        if (!cancelled) setCustomers(data.customers ?? []);
      } catch {
        /* offline — picker stays empty, simulator still runs */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const refetchSaved = async () => {
    try {
      const data = await apiFetch<{ scenarios: SavedScenario[] }>(
        "/api/simulator/scenarios",
      );
      setSavedScenarios(data.scenarios ?? []);
    } catch {
      /* offline — saved list stays empty, starters still work */
    }
  };

  useEffect(() => {
    void refetchSaved();
  }, []);

  const turns = useMemo(() => parseTranscript(draft), [draft]);
  const estimatedSec = Math.round((turns.length * defaultDelayMs) / 1000) + 5;

  // Validation surfaces in the UI as a list of issues — replaces the
  // old binary "Run is disabled" pattern. Empty when transcript is fine.
  const validation = useMemo(() => validateDraft(draft, turns), [draft, turns]);

  // Provenance derived from the loaded source. Sent on every run/start
  // so call_simulator_runs captures where the transcript came from.
  const provenance = useMemo(() => {
    if (loaded === null) {
      return {
        scenario_source: "ad_hoc" as const,
        scenario_label: "Ad-hoc transcript",
        source_was_modified: false,
      };
    }
    if (loaded.kind === "starter") {
      const s = SIM_SCENARIOS.find((x) => x.id === loaded.id);
      const original = s ? scenarioToTranscriptText(s) : "";
      return {
        scenario_source: "starter" as const,
        starter_key: loaded.id,
        scenario_label: s?.label ?? "Unknown starter",
        source_was_modified: draft.trim() !== original.trim(),
      };
    }
    const s = savedScenarios.find((x) => x.id === loaded.id);
    return {
      scenario_source: "saved" as const,
      scenario_id: loaded.id,
      scenario_label: s?.label ?? "Unknown saved",
      scenario_version: s?.updated_at,
      source_was_modified:
        s !== undefined && draft.trim() !== s.transcript_text.trim(),
    };
  }, [loaded, draft, savedScenarios]);

  function clearOutput() {
    setCallId(undefined);
    setErr(null);
  }

  async function runBulk() {
    setErr(null);
    if (turns.length === 0) {
      setErr("Add at least one turn (e.g. 'CUSTOMER: ...').");
      return;
    }
    setRunning(true);
    setCallId(undefined);
    try {
      const r = await fetch(`${API_URL}/api/simulator/run`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          customer_id: customerId === "" ? null : customerId,
          turns,
          default_delay_ms: defaultDelayMs,
          provenance,
        }),
      });
      const data = (await r.json()) as
        | { call_id: number }
        | { error: string; detail?: unknown };
      if (!r.ok || !("call_id" in data)) {
        throw new Error(
          "error" in data
            ? `${data.error}: ${JSON.stringify(data.detail ?? "")}`
            : `HTTP ${r.status}`,
        );
      }
      setCallId(data.call_id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  }

  async function startStep() {
    setErr(null);
    setStepStarting(true);
    setCallId(undefined);
    try {
      const r = await fetch(`${API_URL}/api/simulator/start`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          customer_id: customerId === "" ? null : customerId,
          // Step mode is always ad-hoc at start (no preloaded
          // transcript). The /end PATCH writes the final hash.
          provenance: {
            scenario_source: "ad_hoc",
            scenario_label: "Ad-hoc step session",
            source_was_modified: false,
          },
        }),
      });
      const data = (await r.json()) as
        | { call_id: number }
        | { error: string; detail?: unknown };
      if (!r.ok || !("call_id" in data)) {
        throw new Error(
          "error" in data
            ? `${data.error}: ${JSON.stringify(data.detail ?? "")}`
            : `HTTP ${r.status}`,
        );
      }
      setCallId(data.call_id);
      setStepLive(true);
      setStepRole("customer");
      setStepText("");
      setStepSent([]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setStepStarting(false);
    }
  }

  async function sendStep() {
    if (callId === undefined) return;
    const text = stepText.trim();
    if (!text) return;
    setErr(null);
    setStepSending(true);
    try {
      const r = await fetch(
        `${API_URL}/api/simulator/sessions/${callId}/turn`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ role: stepRole, text }),
        },
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(
          "error" in data
            ? `${(data as { error: string }).error}`
            : `HTTP ${r.status}`,
        );
      }
      setStepSent((cur) => [...cur, { role: stepRole, text }]);
      setStepText("");
      // Most demos alternate speakers — flip the toggle so the next
      // Send naturally picks up from the other side. Operator can
      // override before typing.
      setStepRole((r) => (r === "customer" ? "agent" : "customer"));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setStepSending(false);
    }
  }

  async function endStep() {
    if (callId === undefined) {
      setStepLive(false);
      return;
    }
    try {
      await fetch(`${API_URL}/api/simulator/sessions/${callId}/end`, {
        method: "POST",
      });
    } catch {
      /* best-effort — TTL eviction will catch it on the bot side */
    }
    setStepLive(false);
  }

  return (
    <AppShell>
      <main id="advisor-sim" className="advisor-sim">
        <header className="advisor-sim-header">
          <h1>Advisor simulator</h1>
          <p className="advisor-sim-sub">
            Drive the live advisor with typed input — no audio. Cards
            stream into the right pane the same way they do on a real
            call.
          </p>
        </header>

        <div className="advisor-sim-grid">
          <section className="advisor-sim-input">
            <div className="advisor-sim-tabs-row">
              <div className="advisor-sim-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "bulk"}
                  className={`advisor-sim-tab ${mode === "bulk" ? "is-active" : ""}`}
                  onClick={() => setMode("bulk")}
                  disabled={running || stepLive}
                  title={
                    stepLive
                      ? "End the step session first"
                      : "Bulk replay a full transcript"
                  }
                >
                  Bulk
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "step"}
                  className={`advisor-sim-tab ${mode === "step" ? "is-active" : ""}`}
                  onClick={() => setMode("step")}
                  disabled={running || stepLive}
                  title={
                    running
                      ? "Wait for the bulk run to finish"
                      : "Type one turn at a time"
                  }
                >
                  Step
                </button>
              </div>
              {mode === "bulk" && (
                <LoadedChip
                  loaded={loaded}
                  savedScenarios={savedScenarios}
                  modified={provenance.source_was_modified}
                />
              )}
            </div>

            <label className="advisor-sim-label">
              Customer
              <select
                className="advisor-sim-control"
                value={customerId}
                onChange={(e) =>
                  setCustomerId(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                disabled={running || stepLive}
              >
                <option value="">— none (no offers / no profile) —</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {customerLabel(c)}
                  </option>
                ))}
              </select>
            </label>

            {mode === "bulk" ? (
              <BulkPane
                draft={draft}
                setDraft={setDraft}
                defaultDelayMs={defaultDelayMs}
                setDefaultDelayMs={setDefaultDelayMs}
                turns={turns}
                estimatedSec={estimatedSec}
                running={running}
                callId={callId}
                onRun={runBulk}
                onClear={clearOutput}
                err={err}
                savedScenarios={savedScenarios}
                loaded={loaded}
                setLoaded={setLoaded}
                refetchSaved={refetchSaved}
                validation={validation}
              />
            ) : (
              <StepPane
                live={stepLive}
                starting={stepStarting}
                callId={callId}
                role={stepRole}
                setRole={setStepRole}
                text={stepText}
                setText={setStepText}
                sending={stepSending}
                onStart={startStep}
                onSend={sendStep}
                onEnd={endStep}
                err={err}
                sent={stepSent}
              />
            )}
          </section>

          <section className="advisor-sim-output">
            <div className="advisor-sim-output-cards">
              <h2 className="advisor-sim-output-title">Live cards</h2>
              <div className="advisor-sim-output-cards-body">
                {callId === undefined ? (
                  <div className="advisor-sim-empty">
                    Cards will appear here once a session starts.
                  </div>
                ) : (
                  <LiveAdvice callId={callId} callStatus="active" />
                )}
              </div>
            </div>

            <div className="advisor-sim-output-transcript">
              <h2 className="advisor-sim-output-title">Timeline · turns → cards</h2>
              <div className="advisor-sim-output-transcript-body">
                {callId === undefined ? (
                  <div className="advisor-sim-empty">
                    Each turn appears here with the cards it produced.
                  </div>
                ) : (
                  <Timeline callId={callId} />
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
    </AppShell>
  );
}

// Small chip that surfaces "what's currently loaded into the editor"
// — one of the most common questions when iterating ("am I editing
// AT&T match or did I clear it?"). Shows the source (Starter / Saved
// / New) and an unsaved indicator when the draft diverges from
// whatever loaded. Hidden in step mode (loaded state isn't meaningful
// there).
function LoadedChip({
  loaded,
  savedScenarios,
  modified,
}: {
  loaded: LoadedSource;
  savedScenarios: SavedScenario[];
  modified: boolean;
}) {
  let text: string;
  let kind: "starter" | "saved" | "new";
  if (loaded === null) {
    text = "New scenario";
    kind = "new";
  } else if (loaded.kind === "starter") {
    const s = SIM_SCENARIOS.find((x) => x.id === loaded.id);
    text = `Starter · ${s?.label ?? loaded.id}`;
    kind = "starter";
  } else {
    const s = savedScenarios.find((x) => x.id === loaded.id);
    text = `Saved · ${s?.label ?? `#${loaded.id}`}`;
    kind = "saved";
  }
  return (
    <span className={`advisor-sim-chip advisor-sim-chip-${kind}`}>
      <span className="advisor-sim-chip-label">{text}</span>
      {modified && (
        <span
          className="advisor-sim-chip-modified"
          title="Transcript has been edited from the loaded source"
        >
          ·  modified
        </span>
      )}
    </span>
  );
}

// ─── Bulk-mode pane ────────────────────────────────────────────────────
function BulkPane({
  draft,
  setDraft,
  defaultDelayMs,
  setDefaultDelayMs,
  turns,
  estimatedSec,
  running,
  callId,
  onRun,
  onClear,
  err,
  savedScenarios,
  loaded,
  setLoaded,
  refetchSaved,
  validation,
}: {
  draft: string;
  setDraft: (v: string) => void;
  defaultDelayMs: number;
  setDefaultDelayMs: (v: number) => void;
  turns: Turn[];
  estimatedSec: number;
  running: boolean;
  callId: number | undefined;
  onRun: () => void;
  onClear: () => void;
  err: string | null;
  savedScenarios: SavedScenario[];
  loaded: LoadedSource;
  setLoaded: (v: LoadedSource) => void;
  refetchSaved: () => Promise<void>;
  validation: ValidationIssue[];
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveLabel, setSaveLabel] = useState("");
  const [saveCategory, setSaveCategory] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  // Group starters by category for the dropdown's optgroups.
  const startersByCategory = useMemo(() => {
    const out = new Map<string, SimScenario[]>();
    for (const s of SIM_SCENARIOS) {
      const arr = out.get(s.category) ?? [];
      arr.push(s);
      out.set(s.category, arr);
    }
    return Array.from(out.entries());
  }, []);

  // Same for saved.
  const savedByCategory = useMemo(() => {
    const out = new Map<string, SavedScenario[]>();
    for (const s of savedScenarios) {
      const arr = out.get(s.category) ?? [];
      arr.push(s);
      out.set(s.category, arr);
    }
    return Array.from(out.entries());
  }, [savedScenarios]);

  // Encode dropdown values as `kind:id` so the optgroups can mix
  // starters (string ids) and saved (numeric ids) cleanly.
  const dropdownValue = (() => {
    if (loaded === null) return "";
    return loaded.kind === "starter"
      ? `starter:${loaded.id}`
      : `saved:${loaded.id}`;
  })();

  function handleDropdownChange(raw: string): void {
    if (!raw) return;
    // "+ New scenario" used to live here as a sentinel option but was
    // a redundant path — the standalone "+ New scenario" button does
    // the same thing more visibly. Dropdown is now load-only.
    const [kind, idRaw] = raw.split(":", 2);
    if (kind === "starter" && idRaw) {
      const s = SIM_SCENARIOS.find((x) => x.id === idRaw);
      if (s) {
        setDraft(scenarioToTranscriptText(s));
        setLoaded({ kind: "starter", id: idRaw });
      }
    } else if (kind === "saved" && idRaw) {
      const s = savedScenarios.find((x) => x.id === Number(idRaw));
      if (s) {
        setDraft(s.transcript_text);
        setLoaded({ kind: "saved", id: s.id });
      }
    }
  }

  function newScenario(): void {
    setLoaded(null);
    setDraft("");
    setSaveOpen(false);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }

  // The save form opens with the right defaults depending on what's
  // loaded:
  //   - saved row → "Update <label>", form pre-filled.
  //   - starter loaded or nothing loaded → "Save as scenario", blank form
  //     (saving a starter creates a new copy; starters never mutate).
  function openSaveForm(): void {
    setSaveErr(null);
    if (loaded?.kind === "saved") {
      const s = savedScenarios.find((x) => x.id === loaded.id);
      setSaveLabel(s?.label ?? "");
      setSaveCategory(s?.category ?? "");
    } else if (loaded?.kind === "starter") {
      const s = SIM_SCENARIOS.find((x) => x.id === loaded.id);
      setSaveLabel(s ? `${s.label} (copy)` : "");
      setSaveCategory(s?.category ?? "");
    } else {
      setSaveLabel("");
      setSaveCategory("");
    }
    setSaveOpen(true);
  }

  async function submitSave(): Promise<void> {
    const label = saveLabel.trim();
    const category = saveCategory.trim();
    const transcript = draft.trim();
    if (!label || !category) {
      setSaveErr("Label and category are required.");
      return;
    }
    if (!transcript) {
      setSaveErr("Transcript can't be empty.");
      return;
    }
    setSaving(true);
    setSaveErr(null);
    try {
      const updating = loaded?.kind === "saved";
      const url = updating
        ? `${API_URL}/api/simulator/scenarios/${loaded.id}`
        : `${API_URL}/api/simulator/scenarios`;
      const r = await fetch(url, {
        method: updating ? "PATCH" : "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          label,
          category,
          transcript_text: transcript,
        }),
      });
      const data = (await r.json()) as
        | SavedScenario
        | { error: string; detail?: unknown };
      if (!r.ok || !("id" in data)) {
        throw new Error(
          "error" in data ? data.error : `HTTP ${r.status}`,
        );
      }
      await refetchSaved();
      setLoaded({ kind: "saved", id: data.id });
      setSaveOpen(false);
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function deleteLoaded(): Promise<void> {
    if (loaded?.kind !== "saved") return;
    const s = savedScenarios.find((x) => x.id === loaded.id);
    if (!confirm(`Delete saved scenario "${s?.label ?? loaded.id}"?`)) return;
    try {
      await fetch(`${API_URL}/api/simulator/scenarios/${loaded.id}`, {
        method: "DELETE",
      });
      await refetchSaved();
      setLoaded(null);
      setDraft("");
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : String(e));
    }
  }

  const editingSaved = loaded?.kind === "saved";
  const saveLabelText = editingSaved ? "Update scenario" : "Save scenario";

  return (
    <>
      <div className="advisor-sim-row">
        <label className="advisor-sim-label">
          Scenario
          <select
            className="advisor-sim-control"
            value={dropdownValue}
            onChange={(e) => handleDropdownChange(e.target.value)}
            disabled={running}
          >
            <option value="">— pick a scenario —</option>
            {savedByCategory.length > 0 && (
              <optgroup label="Saved">
                {savedByCategory.flatMap(([cat, items]) =>
                  items.map((s) => (
                    <option key={`saved-${s.id}`} value={`saved:${s.id}`}>
                      {cat} · {s.label}
                    </option>
                  )),
                )}
              </optgroup>
            )}
            {startersByCategory.map(([category, items]) => (
              <optgroup key={category} label={`Starter · ${category}`}>
                {items.map((s) => (
                  <option key={`starter-${s.id}`} value={`starter:${s.id}`}>
                    {s.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>

        <label className="advisor-sim-label advisor-sim-label-narrow">
          Delay between turns
          <select
            className="advisor-sim-control"
            value={defaultDelayMs}
            onChange={(e) => setDefaultDelayMs(Number(e.target.value))}
            disabled={running}
          >
            <option value={500}>0.5s — fastest</option>
            <option value={1500}>1.5s — realistic</option>
            <option value={3000}>3s — paced</option>
            <option value={6000}>6s — exercise cooldowns</option>
          </select>
        </label>
      </div>

      <label className="advisor-sim-label advisor-sim-textarea-label">
        Transcript
        <span className="advisor-sim-hint">
          One turn per line. Prefix each with <code>CUSTOMER:</code> or{" "}
          <code>AGENT:</code>. Lines without a prefix continue the previous
          turn.
        </span>
        <textarea
          ref={textareaRef}
          className="advisor-sim-textarea"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              if (turns.length > 0) onRun();
            }
          }}
          disabled={running}
          rows={14}
          spellCheck={false}
          placeholder={
            "CUSTOMER: my bill is way too high\n" +
            "AGENT: let me look at your account\n" +
            "CUSTOMER: it went up thirty bucks\n" +
            "AGENT: I see — your promo expired\n"
          }
        />
      </label>

      {validation.length > 0 && (
        <ul className="advisor-sim-validation">
          {validation.map((v, i) => (
            <li
              key={i}
              className={`advisor-sim-validation-item advisor-sim-validation-${v.severity}`}
            >
              {v.line ? <span className="advisor-sim-validation-line">L{v.line}</span> : null}
              {v.message}
            </li>
          ))}
        </ul>
      )}

      <div className="advisor-sim-actions">
        <button
          type="button"
          className="advisor-sim-btn advisor-sim-btn-primary"
          onClick={onRun}
          disabled={running || turns.length === 0}
          title={turns.length === 0 ? "Type or load a transcript first" : `⌘↵ to run`}
        >
          {running
            ? "Starting…"
            : turns.length === 0
              ? "Run (load or type a transcript first)"
              : `Run · ${turns.length} turns · ~${estimatedSec}s`}
        </button>
        <button
          type="button"
          className="advisor-sim-btn"
          onClick={newScenario}
          disabled={running}
          title="Clear the textarea and start a new scenario"
        >
          + New scenario
        </button>
        <button
          type="button"
          className="advisor-sim-btn"
          onClick={openSaveForm}
          disabled={running || draft.trim().length === 0}
        >
          {saveLabelText}…
        </button>
        {editingSaved && (
          <button
            type="button"
            className="advisor-sim-btn"
            onClick={deleteLoaded}
            disabled={running}
          >
            Delete
          </button>
        )}
        <button
          type="button"
          className="advisor-sim-btn"
          onClick={onClear}
          disabled={running || callId === undefined}
        >
          Clear output
        </button>
        {err && <span className="advisor-sim-err">{err}</span>}
        {callId !== undefined && !running && (
          <span className="advisor-sim-ok">
            call #{callId} — cards stream as the replay walks the turns
          </span>
        )}
      </div>

      {saveOpen && (
        <div className="advisor-sim-save-form">
          <div className="advisor-sim-save-row">
            <label className="advisor-sim-label">
              Label
              <input
                type="text"
                className="advisor-sim-control"
                value={saveLabel}
                onChange={(e) => setSaveLabel(e.target.value)}
                placeholder="e.g. Bill shock + lawyer threat"
                disabled={saving}
                autoFocus
              />
            </label>
            <label className="advisor-sim-label">
              Category
              <input
                type="text"
                className="advisor-sim-control"
                value={saveCategory}
                onChange={(e) => setSaveCategory(e.target.value)}
                placeholder="Bill shock · Service issue · Risk · …"
                disabled={saving}
                list="advisor-sim-category-suggestions"
              />
              <datalist id="advisor-sim-category-suggestions">
                {Array.from(
                  new Set([
                    ...SIM_SCENARIOS.map((s) => s.category),
                    ...savedScenarios.map((s) => s.category),
                  ]),
                ).map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </label>
          </div>
          <div className="advisor-sim-actions">
            <button
              type="button"
              className="advisor-sim-btn advisor-sim-btn-primary"
              onClick={submitSave}
              disabled={saving}
            >
              {saving ? "Saving…" : editingSaved ? "Update" : "Save"}
            </button>
            <button
              type="button"
              className="advisor-sim-btn"
              onClick={() => setSaveOpen(false)}
              disabled={saving}
            >
              Cancel
            </button>
            {saveErr && <span className="advisor-sim-err">{saveErr}</span>}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Step-mode pane ────────────────────────────────────────────────────
function StepPane({
  live,
  starting,
  callId,
  role,
  setRole,
  text,
  setText,
  sending,
  onStart,
  onSend,
  onEnd,
  err,
  sent,
}: {
  live: boolean;
  starting: boolean;
  callId: number | undefined;
  role: Role;
  setRole: (r: Role) => void;
  text: string;
  setText: (v: string) => void;
  sending: boolean;
  onStart: () => void;
  onSend: () => void;
  onEnd: () => void;
  err: string | null;
  sent: Turn[];
}) {
  if (!live) {
    return (
      <>
        <p className="advisor-sim-hint advisor-sim-step-hint">
          Type one turn at a time. Role auto-flips after each Send;
          dedupe + cooldowns persist across turns.
        </p>
        <div className="advisor-sim-actions">
          <button
            type="button"
            className="advisor-sim-btn advisor-sim-btn-primary"
            onClick={onStart}
            disabled={starting}
          >
            {starting ? "Starting…" : "Start session"}
          </button>
          {err && <span className="advisor-sim-err">{err}</span>}
        </div>
      </>
    );
  }

  // Show last 4 sent turns above the composer so the operator can
  // remember "what did I just say?" without looking at the timeline
  // pane on the right. 4 fits comfortably in the space the previous
  // 14-row textarea was wasting; older turns are still visible in the
  // timeline.
  const recentSent = sent.slice(-4);

  return (
    <>
      <div className="advisor-sim-step-header">
        <span className="advisor-sim-ok">
          live · call #{callId ?? "—"} · {sent.length} turn{sent.length === 1 ? "" : "s"} sent
        </span>
        <button
          type="button"
          className="advisor-sim-btn advisor-sim-btn-small"
          onClick={onEnd}
        >
          End session
        </button>
      </div>

      {recentSent.length > 0 && (
        <div className="advisor-sim-step-rail" aria-label="Recent turns">
          {sent.length > recentSent.length && (
            <div className="advisor-sim-step-rail-trunc">
              … {sent.length - recentSent.length} earlier turn
              {sent.length - recentSent.length === 1 ? "" : "s"}
            </div>
          )}
          {recentSent.map((t, i) => (
            <div
              key={i}
              className={`advisor-sim-step-rail-turn advisor-sim-step-rail-turn-${t.role}`}
            >
              <span className="advisor-sim-step-rail-role">
                {t.role.toUpperCase()}
              </span>
              <span className="advisor-sim-step-rail-text">{t.text}</span>
            </div>
          ))}
        </div>
      )}

      <div className="advisor-sim-step-composer">
        <div className="advisor-sim-step-roles" role="radiogroup" aria-label="Speaker">
          <button
            type="button"
            role="radio"
            aria-checked={role === "customer"}
            className={`advisor-sim-step-role ${role === "customer" ? "is-active" : ""}`}
            onClick={() => setRole("customer")}
          >
            CUSTOMER
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={role === "agent"}
            className={`advisor-sim-step-role ${role === "agent" ? "is-active" : ""}`}
            onClick={() => setRole("agent")}
          >
            AGENT
          </button>
          <span className="advisor-sim-step-roles-hint">
            ⌘↵ to send · role flips after each Send
          </span>
        </div>

        <textarea
          className="advisor-sim-textarea advisor-sim-textarea-step"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              onSend();
            }
          }}
          disabled={sending}
          rows={3}
          spellCheck={false}
          placeholder={
            role === "customer"
              ? "What the customer says next…"
              : "What the agent says next…"
          }
          autoFocus
        />
      </div>

      <div className="advisor-sim-actions">
        <button
          type="button"
          className="advisor-sim-btn advisor-sim-btn-primary"
          onClick={onSend}
          disabled={sending || !text.trim()}
        >
          {sending ? "Sending…" : `Send as ${role}`}
        </button>
        {err && <span className="advisor-sim-err">{err}</span>}
      </div>
    </>
  );
}

// ─── Timeline · turns ↔ cards ──────────────────────────────────────────
//
// The plain transcript-bubble view that AgentDesk uses doesn't tell the
// operator WHICH turn produced WHICH card — exactly the question the
// simulator exists to answer. This view interleaves each transcript
// turn with the advice cards anchored to it via based_on_transcript_id,
// so the question "what did the model do when the customer said X?" is
// readable at a glance.
//
// Subscribes to the same /api/calls/:id/stream SSE feed for both event
// types in a single connection.
type TranscriptRow = {
  id: number;
  ts: string;
  role: "customer" | "agent" | "assistant" | "system";
  text: string;
};

type AdviceRow = {
  id: number;
  ts: string;
  type: string;
  title: string;
  based_on_transcript_id: number | null;
};

const ROLE_LABEL: Record<TranscriptRow["role"], string> = {
  customer: "CUSTOMER",
  agent: "AGENT",
  assistant: "AI",
  system: "SYSTEM",
};

function Timeline({ callId }: { callId: number }) {
  const [transcripts, setTranscripts] = useState<TranscriptRow[]>([]);
  const [advice, setAdvice] = useState<AdviceRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setTranscripts([]);
    setAdvice([]);
    setErr(null);

    (async () => {
      try {
        const [tRes, aRes] = await Promise.all([
          fetch(`${API_URL}/api/calls/${callId}/transcripts`),
          fetch(`${API_URL}/api/calls/${callId}/advice`),
        ]);
        if (!tRes.ok || !aRes.ok)
          throw new Error(`backfill ${tRes.status}/${aRes.status}`);
        const tBody = (await tRes.json()) as { transcripts: TranscriptRow[] };
        const aBody = (await aRes.json()) as { advice: AdviceRow[] };
        if (cancelled) return;
        setTranscripts(tBody.transcripts ?? []);
        setAdvice(aBody.advice ?? []);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      }
    })();

    const es = new EventSource(`${API_URL}/api/calls/${callId}/stream`);
    es.addEventListener("transcript", (ev) => {
      try {
        const row = JSON.parse((ev as MessageEvent).data) as TranscriptRow;
        setTranscripts((cur) =>
          cur.some((x) => x.id === row.id) ? cur : [...cur, row],
        );
      } catch {
        /* skip malformed */
      }
    });
    es.addEventListener("advice", (ev) => {
      try {
        const row = JSON.parse((ev as MessageEvent).data) as AdviceRow;
        setAdvice((cur) =>
          cur.some((x) => x.id === row.id) ? cur : [...cur, row],
        );
      } catch {
        /* skip malformed */
      }
    });
    return () => {
      cancelled = true;
      es.close();
    };
  }, [callId]);

  // Group cards under the transcript they were produced from.
  const cardsByTid = useMemo(() => {
    const m = new Map<number, AdviceRow[]>();
    for (const a of advice) {
      if (a.based_on_transcript_id == null) continue;
      const arr = m.get(a.based_on_transcript_id) ?? [];
      arr.push(a);
      m.set(a.based_on_transcript_id, arr);
    }
    return m;
  }, [advice]);

  // Cards that don't reference a turn — shouldn't happen on the
  // simulator path (every advisor decision is tied to a transcript),
  // but render them at the tail rather than dropping silently.
  const orphanCards = useMemo(
    () => advice.filter((a) => a.based_on_transcript_id == null),
    [advice],
  );

  // Auto-scroll on each new event so the latest action stays visible.
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [transcripts, advice]);

  if (err) {
    return <div className="advisor-sim-empty">timeline error: {err}</div>;
  }
  if (transcripts.length === 0) {
    return <div className="advisor-sim-empty">No turns yet.</div>;
  }

  return (
    <div ref={bodyRef} className="advisor-sim-tl">
      {transcripts.map((t) => {
        const cards = cardsByTid.get(t.id) ?? [];
        return (
          <div
            key={`t-${t.id}`}
            className={`advisor-sim-tl-turn advisor-sim-tl-turn-${t.role}`}
          >
            <div className="advisor-sim-tl-meta">
              <span className="advisor-sim-tl-role">
                {ROLE_LABEL[t.role] ?? t.role.toUpperCase()}
              </span>
              <span className="advisor-sim-tl-time">{fmtTime(t.ts)}</span>
              <span className="advisor-sim-tl-count">
                {cards.length === 0
                  ? "no card"
                  : cards.length === 1
                    ? "1 card"
                    : `${cards.length} cards`}
              </span>
            </div>
            <div className="advisor-sim-tl-text">{t.text}</div>
            {cards.map((a) => (
              <div key={`a-${a.id}`} className="advisor-sim-tl-card">
                <span className="advisor-sim-tl-card-arrow" aria-hidden>↳</span>
                <span className="advisor-sim-tl-card-type">{a.type}</span>
                <span className="advisor-sim-tl-card-title">{a.title}</span>
              </div>
            ))}
          </div>
        );
      })}
      {orphanCards.length > 0 && (
        <div className="advisor-sim-tl-orphans">
          <div className="advisor-sim-tl-meta">
            <span className="advisor-sim-tl-role">UNANCHORED</span>
          </div>
          {orphanCards.map((a) => (
            <div key={`a-${a.id}`} className="advisor-sim-tl-card">
              <span className="advisor-sim-tl-card-arrow" aria-hidden>↳</span>
              <span className="advisor-sim-tl-card-type">{a.type}</span>
              <span className="advisor-sim-tl-card-title">{a.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function customerLabel(c: CustomerOption): string {
  const name =
    [c.first_name, c.last_name].filter(Boolean).join(" ") ||
    c.business_name ||
    "(unnamed)";
  return `${name} · ${c.phone}`;
}
